// src/context/OperatorAuthProvider.tsx
//
// Better Auth producer for the operator portal's AuthContext.
//
// WHY THIS EXISTS: OperatorSignIn.tsx was already built on Better Auth, but
// nothing consumed the session it created. AuthProvider wrapped the OIDC hook
// and RequireAuth called signinRedirect(), so every visit bounced to
// auth.citstime.app before /signin could render. The sign-in page was
// unreachable code.
//
// This mirrors the shape AuthContext already publishes (isAuthenticated,
// isLoading, user, token, getToken, login, logout) so no consuming page
// changes, and adds the one fact the operator portal needs that a tenant
// portal does not: whether a second factor is actually enrolled.
//
// TRANSPORT: bearer token, not a cookie. This portal is a different origin
// from the API, so a session cookie would be third-party — see the note in
// src/auth/betterAuthClient.ts.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { authClient, getOperatorToken, setOperatorToken } from '../auth/betterAuthClient'
import api from '../api/axios'
import { AuthContext } from './AuthContext'

export interface Operator {
  id: string
  email: string
  name: string
  stackId: string
  role?: 'owner' | 'admin' | 'viewer' | 'developer'
  can_access_database_tools?: boolean
  can_manage_users?: boolean
  can_modify_settings?: boolean
  permissions?: string[]
}

export interface OperatorAuthValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: Operator | null
  token: string | null
  /**
   * Has this operator actually enrolled a second factor?
   *
   * Kept separate from isAuthenticated on purpose. A password-only operator IS
   * authenticated — they simply may not perform the step-up-gated actions
   * (impersonation, session revocation, migration import, switching a
   * company's auth provider). Collapsing the two would either lock them out of
   * the whole portal or let them past the gate; neither is right.
   */
  twoFactorEnabled: boolean
  getToken: () => Promise<string | null>
  login: () => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  m365Account: unknown
}

const Ctx = createContext<OperatorAuthValue | undefined>(undefined)

export function useOperatorAuth(): OperatorAuthValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useOperatorAuth must be used within OperatorAuthProvider')
  return v
}

export function OperatorAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Operator | null>(null)
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const loadedFor = useRef<string | null>(null)

  const clear = useCallback(() => {
    setUser(null)
    setTwoFactorEnabled(false)
    loadedFor.current = null
    setOperatorToken(null)
  }, [])

  const load = useCallback(async () => {
    try {
      const { data } = await authClient.getSession()
      const sessionUser = data?.user
      if (!sessionUser?.email) {
        clear()
        return
      }

      setTwoFactorEnabled(Boolean((sessionUser as { twoFactorEnabled?: boolean }).twoFactorEnabled))

      // /super-admin/auth/verify is the authority on operator permissions —
      // registry_super_admin_users, not anything Better Auth stores. A valid
      // session for someone who is not an operator must NOT produce a user.
      try {
        const resp = await api.get('/super-admin/auth/verify')
        const u = resp.data?.user || {}
        setUser({
          id: u.id || sessionUser.id,
          email: u.email || sessionUser.email,
          name: u.name || sessionUser.name || sessionUser.email,
          stackId: u.zitadelUserId || u.stackUserId || sessionUser.id,
          role: u.role || 'owner',
          can_access_database_tools: u.can_access_database_tools ?? true,
          can_manage_users: u.can_manage_users ?? true,
          can_modify_settings: u.can_modify_settings ?? true,
          permissions: u.permissions || ['manage_users', 'modify_settings', 'access_database_tools'],
        })
        loadedFor.current = sessionUser.id
      } catch (err) {
        const e = err as { response?: { status?: number; data?: { code?: string } } }
        const status = e?.response?.status
        const code = e?.response?.data?.code

        // NOT the same refusal, and treating them alike is a sign-in loop.
        //
        // authenticateSuperAdmin returns 403 for TWO different reasons, and
        // only one of them means "you do not belong here":
        //
        //   code: 'two_factor_required'  -> a real operator who has not enrolled
        //                                   yet. Every /super-admin route refuses
        //                                   them, including this one, until they
        //                                   do — but they ARE authenticated.
        //   any other 403                -> the email is not in
        //                                   registry_super_admin_users at all.
        //
        // Signing out the first case bounced the operator to /signin, where a
        // correct password produced a session that failed verify again. The
        // only way out is to let them in unverified and route them to
        // enrolment, which is what twoFactorEnabled=false + RequireAuth does.
        if (status === 403 && code === 'two_factor_required') {
          setTwoFactorEnabled(false)
          setUser({
            id: sessionUser.id,
            email: sessionUser.email,
            name: sessionUser.name || sessionUser.email,
            stackId: sessionUser.id,
            // No operator permissions are granted until verify succeeds. The
            // portal is unusable in this state by design; only /security is.
            role: undefined,
            can_access_database_tools: false,
            can_manage_users: false,
            can_modify_settings: false,
            permissions: [],
          })
          loadedFor.current = sessionUser.id
          return
        }

        if (status === 401 || status === 403) {
          console.warn('[OperatorAuth] session is not a platform operator; signing out')
          await authClient.signOut().catch(() => {})
          clear()
          return
        }
        // A transient backend error must not look like a sign-out.
        console.error('[OperatorAuth] verify failed, keeping session', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [clear])

  useEffect(() => { void load() }, [load])

  const getToken = useCallback(async () => getOperatorToken(), [])

  const login = useCallback(async () => {
    window.location.assign('/signin')
  }, [])

  const logout = useCallback(async () => {
    try {
      await authClient.signOut()
    } catch (err) {
      console.error('[OperatorAuth] sign-out failed, clearing locally', err)
    }
    clear()
    window.location.assign('/signin')
  }, [clear])

  const value = useMemo<OperatorAuthValue>(() => ({
    isAuthenticated: !!user,
    isLoading,
    user,
    token: getOperatorToken(),
    twoFactorEnabled,
    getToken,
    login,
    logout,
    refresh: load,
    m365Account: null,
  }), [user, isLoading, twoFactorEnabled, getToken, login, logout, load])

  // Published into BOTH contexts: useOperatorAuth() for the new screens that
  // need twoFactorEnabled, and the original AuthContext so Sidebar, Settings,
  // UserManagement and the rest keep working untouched.
  return (
    <Ctx.Provider value={value}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </Ctx.Provider>
  )
}

export default OperatorAuthProvider
